import {isAnswered, createElement, notify} from './utils.js';
import text from './constants.js';
import renderIcon from './renderers/renderIcon.js';
import componentRenderers from './renderers/componentRenderers.js';
import componentExtractors from './extractors/componentExtractors.js';
import componentAssessors from './assessors/componentAssessors.js';
import defaultComponents from './components.js';

export default class Base {
    constructor(containerId, options = {}){
        this.container = document.querySelector(containerId);
        if (!this.container) {
            throw new TypeError(text.CONTAINER_WARNING);
        }

        this.options = options;

        this.mode = this.options.mode ?? 1;
        this.hasFooter = this.options.hasFooter ?? true;
        this.isPreview = this.options.isPreview ?? false;
        this.scoring = this.options.scoring ?? true;

        this.initialData = this.options.initialData ?? [];
        this.sendToServer = this.options.sendToServer ?? false;

        this.defaults = {
            render: 'renderInput', // To create elements in each mode
            extract: 'extractInput', // To get answer/correct answer from component
            assess: 'assessInput' // To assess/compare the answer to correct answer for calculating the earned point
        };

        this.draggedComponent = null;

        this.render();
    }

    render() {
        // Default components
        this.componentTypes = defaultComponents;

        if (this.options.components) {
            this.componentTypes = this.options.components.map(c => {
                if (typeof c === 'string') {
                    return defaultComponents.find(d => d.type === c);
                }
                else if (typeof c === 'object' && c.type) {
                    const def = defaultComponents.find(d => d.type === c.type);
                    return { ...def, ...c };
                }
                return null;
            }).filter(Boolean);
        }

        this.form = null;
        if(this.mode == 2 && this.options.saveUrl || false){
            this.form = createElement('form', {
                name: 'form_' + Date.now(),
                action: this.options.saveUrl ?? '#'
            })
            this.container.before(this.form);
            this.form.appendChild(this.container);    
        }
        this.container.classList.add('x-main');
        this.container.innerHTML = '';

        // Main header
        this.header = createElement('div', {
            className: 'x-header'
        });
        ['title', 'description'].forEach(x => {
            const el = createElement('div', {
                className: 'x-' + x,
                innerHTML: 'Form ' + x.charAt(0).toUpperCase() + x.slice(1)
            })
            if(this.initialData[x]) el.innerHTML = this.initialData[x];
            if(this.mode==1) el.contentEditable = true;
            this.header.appendChild(el);
        });
        this.container.appendChild(this.header);

        // Body: consist of sidebar and content
        const body = createElement('div', {
            className: 'x-body'
        });
        if(!this.scoring && this.mode != 1) body.classList.add('x-nosidebar');
        body.appendChild(this.renderSidebar());        

        // Content is in which questions/components are placed
        this.content = this.renderContent();
        body.appendChild(this.content);

        if((this.initialData.components ?? false) && this.initialData.components.length){
            this.initialData.components.forEach(x => {
                this.addComponent(x.type, x);
            })
            if(this.scoring && this.mode == 2){
                this.curValue.innerText = this.initialData.components.reduce((total, d) => {
                    const value = Number(isAnswered(d.answer ?? false));
                    return total + (isNaN(value) ? 0 : value);
                }, 0);
                this.refValue.innerText = this.initialData.components.filter(d => typeof d.max_point!='undefined' ).length;
            }
            if(this.scoring && this.mode >= 3){
                this.curValue.innerText = this.initialData.components.reduce((total, d) => {
                    const value = Number(d.assess_point ?? 0);
                    return total + (isNaN(value) ? 0 : value);
                }, 0);                
                this.refValue.innerText = this.initialData.components.reduce((total, d) => {
                    const value = Number(d.max_point ?? 0);
                    return total + (isNaN(value) ? 0 : value);
                }, 0);
            }            
        }
        this.container.appendChild(body);

        // Footer
        if(this.hasFooter && this.mode <= 3){
            const footer = createElement('div', {
                className: 'x-main-footer'
            });
            this.container.appendChild(footer);    
            if(this.mode <= 2){
                let previewButton;
                if(previewButton = this.renderPreviewButton()) {
                    footer.appendChild(previewButton);
                }

                if(this.mode == 1) {
                    const clear = createElement('button', {
                        className: 'x-button',
                        innerHTML: text.CLEAR,
                        on_click: () => {
                            if(confirm(text.CLEAR_CONFIRM)){
                                this.content.innerHTML = "";
                            }
                        }       
                    });
                    footer.appendChild(clear);                    
                }
            }
            if(this.options.saveUrl || false){
                const submit = createElement('button', {
                    className: 'x-button',
                    innerHTML: text.SUBMIT,
                    on_click: (e) => {                    
                        e.preventDefault();
                        let data = this.getJson(true);
                        if(this.mode == 2)
                        {
                            if(!data.isValid){
                                notify(text.DATA_INCOMPLETE);
                                return;                                
                            } 
                            data = new FormData(this.form);   
                        } else {
                            data = JSON.stringify(data);
                        }
                        this.sendData(data);    
                        notify(text.DATA_SUBMITTED);
                    }
                });
                footer.appendChild(submit);
            }            
        }
    }

    async sendData(formData){
        try {
            if(this.sendToServer) formData.sendToServer = sendToServer;

            const response = await fetch(this.options.saveUrl, {
                method: 'POST',
                body: formData,
            });
            
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }
            
            const result = await response.json();
            if(result) notify(result);
        } catch (error) {
            console.error('Error:', error.message);
        }
    }

    renderSidebar() {
        const sidebar = createElement('div', {
            className: 'x-sidebar'
        });                
        if(this.mode == 1){
            this.componentTypes.forEach(x => {
                const el = createElement('div', {
                    className: 'x-componentBtn',
                    title: x.label, 
                    draggable: true,
                    innerHTML: renderIcon(x.icon),
                    on_click: () => {
                        const newComponent = this.addComponent(x.type)
                        setTimeout(() => {
                            newComponent.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 50);                
                    },
                    on_dragstart: e => {
                        this.content.classList.add('x-drag-over');                    
                        e.dataTransfer.setData('type', x.type)
                        e.dataTransfer.setData("component", "new");
                        this.draggedComponent = el;
                    },
                    on_dragend: e => {
                        this.content.classList.remove('x-drag-over');                    
                    }
                });
                sidebar.appendChild(el);
            })
        } else {
            if (!this.scoring) {
                sidebar.classList.add('x-empty')
                return sidebar;
            }
            let labels = [text.ASSESS_POINT, text.MAX_POINT];
            if(this.mode == 2) labels = [text.ANSWERS, text.QUESTIONS];
            this.curValue = createElement('div', {
                className: 'x-current-value',
                title: labels[0],
                innerText: '0'
            });
            sidebar.appendChild(this.curValue);
            this.refValue = createElement('div', {
                className: 'x-ref-value',
                title: labels[1],
                innerText: '0'
            });
            sidebar.appendChild(this.refValue);            
        }
        return sidebar;
    }

    renderContent() {
        const content = createElement('div', {
            className: 'x-content'
        });
        if(this.mode <= 3){
            const contentHeight = this.options.height ?? 500;
            if(contentHeight===0){
                content.style.height = '100vh';
            } else {
                content.style.height = contentHeight + 'px';
            }    
        }
        content.addEventListener('dragenter', e => e.preventDefault());
        content.addEventListener('dragover', e => {
            if (!e.dataTransfer.types.includes("component")) {
                return;
            }
    
            e.preventDefault();
    
            const makePlaceholder = (draggedComponent) => {
                return createElement("div", {
                    className: 'x-placeholder',
                    style: {
                        height: `${draggedComponent.offsetHeight}px`
                    }
                });
            };
    
            const draggedComponent = this.draggedComponent;
            const content = e.currentTarget;
            const components = content.children;
            const existingPlaceholder = content.querySelector(".x-placeholder");
            if (existingPlaceholder) {
                const placeholderRect = existingPlaceholder.getBoundingClientRect();
                if (
                    placeholderRect.top <= e.clientY &&
                    placeholderRect.bottom >= e.clientY
                ) {
                    return;
                }
            }
            for (const component of components) {
                if (component.getBoundingClientRect().bottom >= e.clientY) {
                    if (component === existingPlaceholder) return;
                    existingPlaceholder?.remove();
                    if (component === draggedComponent || component.previousElementSibling === draggedComponent) return;
                    content.insertBefore(
                        existingPlaceholder ?? makePlaceholder(draggedComponent),
                        component,
                    );
                    return;
                }
            }
            existingPlaceholder?.remove();
            if (content.lastElementChild === draggedComponent) return;
            content.append(existingPlaceholder ?? makePlaceholder(draggedComponent));                    
        });
        content.addEventListener('dragleave', e => {
            if (content.contains(e.relatedTarget)) return;
            const placeholder = content.querySelector('.x-placeholder');
            placeholder?.remove();
        });        
        content.addEventListener('drop', e => {
            if(!e.dataTransfer.types.includes('component')) return;
            e.preventDefault();

            const isNew = e.dataTransfer.getData('component');
            const placeholder = content.querySelector(".x-placeholder");

            let draggedComponent = this.draggedComponent;
            if(isNew == 'new'){
                const type = e.dataTransfer.getData('type');
                draggedComponent = this.addComponent(type);
            }
            if (!placeholder) return;
            draggedComponent.remove();
            content.insertBefore(draggedComponent, placeholder);
            placeholder.remove();
        });
        return content;
    }

    renderPreviewButton () {
        if(this.mode == 2 && !this.isPreview) return false;
        const label = this.isPreview ? text.EDIT : text.PREVIEW;
        const button = createElement('button', {
            className: 'x-button x-button-preview',
            innerHTML: label,
            dataset: {
                type: label.toLowerCase()
            },
            on_click: (e) => {
                const el = e.target;
                const type = el.dataset.type;
                if(type == 'preview') {
                    this.initialData = this.getJson();
                    this.mode = 2;
                    this.container.innerHTML = '';
                    this.isPreview = true;
                    this.render();
                } else {
                    this.mode = 1;
                    this.container.innerHTML = '';
                    this.isPreview = false;
                    this.render();                
                }
            }
        });
        
        return button;
    }

    /**
     * 
     * @param {string} type 
     * @param {object} data 
     * @returns HTMLNode
     */
    addComponent(type, data = {}) {
        const componentProperties = this.componentTypes.find(d => d.type === type);
        data.label = data.label ? data.label : componentProperties.label;

        data.id = data.id ? data.id : type + '_' + Date.now();
        const component = createElement('div', {
            className: this.mode == 1 ? 'x-component' : 'x-component x-view',
            id: data.id,
            dataset: { type: type }
        });
        if(this.mode == 1){
            component.classList.add('x-draggable');
            component.draggable = true;
            component.addEventListener("dragstart", (e) => {
                this.draggedComponent = component;
                component.classList.add('x-dragged');
                this.content.classList.add('x-drag-over');
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("component", "");
            });
            component.addEventListener("dragend", (e) => {
                component.classList.remove('x-dragged');
                this.content.classList.remove('x-drag-over');
                this.draggedComponent = null;
            });    
        }

        if(this.mode == 2){
            const isRequired = (data.required ?? false) ? 1 : 0;
            component.dataset.required = isRequired;
            if(isRequired){
                const required_icon = createElement('div', {
                    className: 'x-required-icon',
                    title: text.REQUIRED,
                    innerHTML: renderIcon('required', { width:'8px', style:'fill:red;' })
                });
                component.appendChild(required_icon);
            }
        }

        if(isAnswered(data.answer ?? false)) component.dataset.answered = 1;

        let renderComponent = componentProperties.render ?? this.defaults.render;
        if (typeof renderComponent == 'string') renderComponent = componentRenderers[renderComponent] ?? false;
        if (typeof renderComponent == 'function') renderComponent(component, componentProperties, data, this);

        return component;
    }

    /**
     * 
     * @param {boolean} current 
     * If true => only return current json, not merging with existing initialData
     * If false => the returned json will be merged with existing initialData
     * 
     * @returns json data of the form
     */
    getJson(current = false) {
        const title = this.header.querySelector('.x-title');
        const description = this.header.querySelector('.x-description');
        const returnedData = {
            title: title.innerHTML,
            description: description.innerHTML,
            components: []
        };

        let formValid = true;
        const components = this.container.querySelectorAll('.x-component');
        returnedData.components = Array.from(components).map(component => {
            const type = component.dataset.type;              
            const labelEl = component.querySelector('.x-label');

            const currentData = {
                id: component.id,
                type: type,
                label: labelEl.innerText,
            }

            if(this.mode == 1){
                const requiredEl = component.querySelector('.x-required');
                if(requiredEl) currentData.required = requiredEl.checked;
                const helpEl = component.querySelector('.x-help');
                if(helpEl) currentData.help = helpEl.innerText.trim() == text.HELP ? '' : helpEl.innerText.trim();
            }

            if(this.scoring && this.mode != 2){
                const pointEl = component.querySelector('.x-point');
                if(pointEl) currentData.max_point = parseInt(pointEl.innerText);
            }

            if(this.scoring && this.mode > 2){
                const assesPointEl = component.querySelector('.x-point-assessment');
                if(assesPointEl) currentData.assess_point = parseInt(assesPointEl.innerText);
            }            

            const componentProps = this.componentTypes.find(d => d.type === type);
            if (componentProps) {
                let componentData = currentData;
                if(Object.keys(this.initialData).length && this.initialData.components.length){
                    const d = this.initialData.components.find(c => c.id == component.id);
                    componentData = {...componentData, ...d};
                }

                if(this.mode <= 2) {
                    if(componentProps.extract!==false){
                        let extractor = componentProps.extract || this.defaults.extract;
                        if (typeof extractor == 'string') extractor = componentExtractors[extractor] ?? false;
                        if (typeof extractor == 'function') {
                            const ext_data = extractor(component, componentProps, componentData, this);
                            for (const x in ext_data) {
                                currentData[x] = componentData[x] = ext_data[x];
                            }
                        }   
                        // Check form validity (mode 2)
                        if(component.dataset.required=='1' && !isAnswered(currentData.answer ?? false)) formValid = false;
                    }
                }

                if(this.scoring && (this.mode == 2 || this.mode == 3)) {
                    if(componentProps.assess!==false){
                        let assessor = componentProps.assess || this.defaults.assess;
                        if (typeof assessor == 'string') assessor = componentAssessors[assessor] ?? false;
                        if (typeof assessor == 'function') {
                            const assess_data = assessor(component, componentProps, componentData, this);
                            for (const x in assess_data) {
                                currentData[x] = assess_data[x];
                            }
                        }        
                    }
                }
            }
            return currentData;
        });

        if(!current && Object.keys(this.initialData).length){
            returnedData.components.forEach((x, i) => {
                const comp = this.initialData.components.find(d => d.id === x.id);
                if(comp) returnedData.components[i] = { ...comp, ...x };
            })
        }
        if(this.mode == 2) returnedData.isValid = formValid;

        return returnedData;
    }  

}